import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

export default function CalendarPage() {
  const [cur, setCur] = useState(new Date());
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data));
  }, []);

  const grid = useMemo(() => {
    const start = startOfMonth(cur);
    const startWeekday = start.getDay();
    const total = daysInMonth(cur);
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(cur.getFullYear(), cur.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cur]);

  function jobsOn(date) {
    if (!date) return [];
    const iso = date.toISOString().slice(0, 10);
    return jobs.filter((j) => j.date === iso);
  }

  const monthLabel = cur.toLocaleString("default", { month: "long", year: "numeric" });

  return (
    <div className="p-8 lg:p-10" data-testid="calendar-page">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Schedule</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">{monthLabel}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))}
            className="p-2 border border-slate-300 rounded-md hover:bg-slate-50"><ChevronLeft className="w-4 h-4"/></button>
          <button onClick={() => setCur(new Date())} className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Today</button>
          <button data-testid="cal-next" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))}
            className="p-2 border border-slate-300 rounded-md hover:bg-slate-50"><ChevronRight className="w-4 h-4"/></button>
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <div key={d} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const today = d && d.toDateString() === new Date().toDateString();
            const items = jobsOn(d);
            return (
              <div key={i} className={`min-h-[110px] border-b border-r border-slate-100 p-2 ${d ? "" : "bg-slate-50/40"}`}>
                {d && (
                  <>
                    <div className={`text-xs font-medium mb-1 ${today ? "text-blue-600" : "text-slate-700"}`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {items.slice(0, 3).map((j) => (
                        <Link key={j.id} to={`/jobs/${j.id}`}
                          className={`block text-xs truncate rounded px-1.5 py-0.5 status-${j.status}`}>
                          {j.time} {j.customer_name}
                        </Link>
                      ))}
                      {items.length > 3 && <div className="text-xs text-slate-400">+{items.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
